import { getSupabase } from './client';
import { requireUserId } from './auth';
import { openMeetingRows } from '../crypto/openContent';
import { apiRequest } from '../api';
import { t } from '../language';
import { removeRecording, resolveRecordingPath, safeRecordingPath } from '../recordings';
import type { TrashedMeetingDTO } from '../../shared/types';

/**
 * Çöp kutusu. "Sil" toplantıyı çöpe atar (`soft_delete_meetings`, meetingsRepo);
 * çöpteki toplantı 30 gün sonra sunucunun gece temizliğiyle dosyalarıyla
 * birlikte kalıcı silinir (web/lib/jobs/purge.ts). Bu sürede geri alınabilir ya
 * da buradan hemen kalıcı silinebilir.
 *
 * Silinmiş satırlar RLS'te görünmediği için liste ve geri alma security definer
 * fonksiyonlarla (migration 0032); kalıcı silme dosyalar R2'de olduğu için
 * sunucu ucundan (`POST /api/v1/meetings/purge`).
 */

/** web/lib/jobs/purge.ts'teki DELETED_MEETING_RETENTION_DAYS ile aynı. */
export const TRASH_RETENTION_DAYS = 30;

interface TrashRow {
  id: string;
  title: string | null;
  source: string | null;
  duration_seconds: number | null;
  recorded_at: string | null;
  created_at: string;
  deleted_at: string;
  audio_file_path: string | null;
}

async function listRows(): Promise<TrashRow[]> {
  const { data, error } = await getSupabase().rpc('list_deleted_meetings');
  if (error) {
    console.error('[trash] liste okunamadı:', error.message);
    throw new Error(t('Çöp kutusu yüklenemedi.', 'Could not load the trash.'));
  }
  // Başlık şifreli olabilir (tn1).
  return (await openMeetingRows((data ?? []) as Array<TrashRow & Record<string, unknown>>, await requireUserId())) as TrashRow[];
}

export async function listTrash(): Promise<TrashedMeetingDTO[]> {
  return (await listRows()).map((row) => ({
    id: row.id,
    title: row.title || t('Adsız toplantı', 'Untitled meeting'),
    source: row.source ?? 'desktop',
    durationSeconds: Math.max(0, Math.round(Number(row.duration_seconds ?? 0))),
    recordedAtISO: row.recorded_at ?? row.created_at,
    deletedAtISO: row.deleted_at,
    purgeAtISO: new Date(Date.parse(row.deleted_at) + TRASH_RETENTION_DAYS * 86_400_000).toISOString(),
  }));
}

/** Toplantıları çöpten çıkarır; listeye geri döner. */
export async function restoreMeetings(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  const { data, error } = await getSupabase().rpc('restore_meetings', { ids });
  if (error) {
    console.error('[trash] geri alınamadı:', error.message);
    throw new Error(t('Toplantı geri alınamadı.', 'Could not restore the meeting.'));
  }
  const restored = (data as string[] | null)?.length ?? 0;
  if (restored !== ids.length) {
    throw new Error(
      t(
        `${ids.length - restored} toplantı geri alınamadı; kalıcı silinmiş olabilir.`,
        `${ids.length - restored} meeting(s) could not be restored — they may have been permanently deleted.`
      )
    );
  }
}

/**
 * Kalıcı silme — geri dönüşü yok. Sunucu dosyaları ve satırı siler; arşive
 * yüklenmemiş bir kayıt bu bilgisayarda duruyorsa o da silinir.
 * Silinen kimlikleri döndürür.
 */
export async function purgeMeetings(ids: string[]): Promise<string[]> {
  if (ids.length === 0) return [];
  const localPaths = new Map((await listRows()).map((row) => [row.id, row.audio_file_path]));

  const { purged } = await apiRequest<{ purged: string[] }>('POST', '/api/v1/meetings/purge', {
    body: { ids },
    timeoutMs: 60_000,
  });

  for (const id of purged) {
    // Yol kullanıcının yazabildiği bir alandan geliyor: yalnızca kayıt klasörü içindeki ses dosyası (SEC-07).
    const local = await safeRecordingPath(resolveRecordingPath(localPaths.get(id) ?? undefined));
    if (local) await removeRecording(local).catch((err) => console.warn('[trash] yerel kayıt silinemedi:', err));
  }
  if (purged.length < ids.length) {
    throw new Error(
      t(
        `${ids.length - purged.length} toplantı kalıcı silinemedi, biraz sonra tekrar dene.`,
        `${ids.length - purged.length} meeting(s) could not be permanently deleted — try again shortly.`
      )
    );
  }
  return purged;
}
